import { ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR } from './adminProspectQuickFollowUp.js';

export const ADMIN_PROSPECT_RESPONSE_NOTIFICATIONS_REFRESH_MS = 60_000;

/**
 * HashRouter-safe location state for opening a public response on Prospect 360.
 * Never encode a document fragment on the Link `to` prop.
 * @param {{ campaignId?: string | null, mailingId?: string | null, intent?: string | null }} item
 */
export function buildOpenResponseLinkState(item = {}) {
  return {
    focusPublicResponseAnchor: ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR,
    ...(item.campaignId
      ? { focusPublicResponseCampaignId: item.campaignId }
      : {}),
    ...(item.mailingId ? { focusPublicResponseMailingId: item.mailingId } : {}),
    ...(item.intent ? { focusPublicResponseIntent: item.intent } : {}),
  };
}

/**
 * Whether a delayed async notification result may still mutate UI.
 * @param {{
 *   mounted: boolean,
 *   requestGeneration: number,
 *   activeGeneration: number,
 *   requestAuthUid?: string | null,
 *   activeAuthUid?: string | null,
 * }} args
 */
export function shouldApplyNotificationResult({
  mounted,
  requestGeneration,
  activeGeneration,
  requestAuthUid,
  activeAuthUid,
}) {
  if (!mounted) return false;
  if (requestGeneration !== activeGeneration) return false;
  const requestUid = String(requestAuthUid || '');
  const activeUid = String(activeAuthUid || '');
  if (requestUid !== activeUid) return false;
  return true;
}

/**
 * @param {string | null | undefined} intent
 * @param {(key: string) => string} t
 */
export function adminProspectResponseNotificationIntentLabel(intent, t) {
  if (!intent) return '';
  const key = `admin.notifications.intent.${intent}`;
  const label = t(key);
  return label === key ? intent : label;
}

/** @returns {Map<string, number>} */
export function createNotificationMarkingClaimsMap() {
  return new Map();
}

/**
 * Claim busy ownership for a notification id. Returns claim id.
 * @param {Map<string, number>} claims
 * @param {string} id
 * @param {{ nextClaimId: () => number }} seq
 */
export function claimNotificationMarking(claims, id, seq) {
  const key = String(id || '');
  if (!key || !claims) return null;
  const claimId = seq.nextClaimId();
  claims.set(key, claimId);
  return claimId;
}

/**
 * Clear busy only when this claim still owns the id.
 * @param {Map<string, number>} claims
 * @param {string} id
 * @param {number | null} claimId
 */
export function releaseNotificationMarkingClaim(claims, id, claimId) {
  const key = String(id || '');
  if (!claims || !key || claimId == null) return false;
  if (claims.get(key) !== claimId) return false;
  claims.delete(key);
  return true;
}

/** @param {Map<string, number> | null | undefined} claims */
export function clearNotificationMarkingClaims(claims) {
  claims?.clear?.();
}

/** @param {Map<string, number> | null | undefined} claims */
export function notificationMarkingIdsFromClaims(claims) {
  return new Set(claims ? [...claims.keys()] : []);
}

/**
 * Append or replace notification rows, deduping by id (incoming wins on conflict).
 * @param {Array<{ id?: string | null }>} previous
 * @param {Array<{ id?: string | null }>} incoming
 * @param {{ append?: boolean }} [opts]
 */
export function mergeNotificationItemsById(previous, incoming, { append = false } = {}) {
  const nextRows = Array.isArray(incoming) ? incoming : [];
  if (!append) return nextRows;
  const prevRows = Array.isArray(previous) ? previous : [];
  const byId = new Map();
  const order = [];
  for (const row of prevRows) {
    const id = row?.id ? String(row.id) : null;
    if (!id) {
      order.push({ key: `anon:${order.length}`, row });
      continue;
    }
    if (!byId.has(id)) order.push({ key: id, row });
    byId.set(id, row);
  }
  for (const row of nextRows) {
    const id = row?.id ? String(row.id) : null;
    if (!id) {
      order.push({ key: `anon:${order.length}`, row });
      continue;
    }
    if (byId.has(id)) {
      byId.set(id, row);
      const idx = order.findIndex((entry) => entry.key === id);
      if (idx >= 0) order[idx] = { key: id, row };
    } else {
      byId.set(id, row);
      order.push({ key: id, row });
    }
  }
  return order.map((entry) => (byId.has(entry.key) ? byId.get(entry.key) : entry.row));
}

/**
 * Force acknowledged session reads onto list rows so stale list responses cannot
 * resurrect unread=true after a successful mark-read.
 * @param {Array<object>} items
 * @param {Map<string, string>} acknowledgedReadAtById
 */
export function reconcileNotificationItemsWithAcknowledgedReads(items, acknowledgedReadAtById) {
  const rows = Array.isArray(items) ? items : [];
  if (!acknowledgedReadAtById || acknowledgedReadAtById.size === 0) return rows;
  return rows.map((item) => {
    const id = item?.id ? String(item.id) : '';
    if (!id || !acknowledgedReadAtById.has(id)) return item;
    return {
      ...item,
      unread: false,
      readAt: acknowledgedReadAtById.get(id) || item.readAt || null,
    };
  });
}

/**
 * Soft first-page refresh: update/prepend head, keep previously loaded rows
 * (including ones displaced off the first page), and preserve the loaded-tail
 * continuation cursor. Absence from a refreshed first page is not deletion.
 * @param {{
 *   previousItems: Array<object>,
 *   headIds?: Set<string>,
 *   firstPageItems: Array<object>,
 *   firstPageNextToken: string | null,
 *   previousNextPageToken: string | null,
 *   hasLoadedMore: boolean,
 * }} args
 */
export function mergeSoftRefreshNotificationPages({
  previousItems,
  firstPageItems,
  firstPageNextToken,
  previousNextPageToken,
  hasLoadedMore,
} = {}) {
  const prev = Array.isArray(previousItems) ? previousItems : [];
  const head = Array.isArray(firstPageItems) ? firstPageItems : [];
  const newHeadIds = new Set(
    head.map((row) => (row?.id ? String(row.id) : '')).filter(Boolean),
  );
  // Keep every previously loaded row not present in the new first page, in prior order.
  const preserved = prev.filter((row) => {
    const id = row?.id ? String(row.id) : '';
    return id ? !newHeadIds.has(id) : true;
  });
  const shouldPreserveCursor = Boolean(hasLoadedMore) || preserved.length > 0;
  return {
    items: [...head, ...preserved],
    headIds: newHeadIds,
    nextPageToken: shouldPreserveCursor
      ? previousNextPageToken ?? null
      : firstPageNextToken ?? null,
    hasLoadedMore: Boolean(hasLoadedMore) || preserved.length > 0,
  };
}

/**
 * Apply mark-read API rows to list items. Does not touch unread counts.
 * @param {Array<object>} items
 * @param {Array<{ id?: string | null, ok?: boolean, readAt?: string | null }>} results
 */
export function applyMarkReadResultsToItems(items, results) {
  const readAtById = new Map();
  for (const row of results || []) {
    if (row?.ok && row.id) {
      readAtById.set(String(row.id), row.readAt || new Date().toISOString());
    }
  }
  if (!readAtById.size) return Array.isArray(items) ? items : [];
  return (items || []).map((item) => {
    const id = item?.id ? String(item.id) : '';
    if (!readAtById.has(id)) return item;
    return {
      ...item,
      unread: false,
      readAt: readAtById.get(id),
    };
  });
}

/**
 * Remember successful mark-read results for the authorized session.
 * @param {Map<string, string>} acknowledgedReadAtById
 * @param {Array<{ id?: string | null, ok?: boolean, readAt?: string | null }>} results
 */
export function rememberAcknowledgedNotificationReads(acknowledgedReadAtById, results) {
  if (!acknowledgedReadAtById) return;
  for (const row of results || []) {
    if (!row?.ok || !row.id) continue;
    acknowledgedReadAtById.set(
      String(row.id),
      row.readAt || new Date().toISOString(),
    );
  }
}

/**
 * Production orchestration used by AdminProspectResponseNotificationsBell.
 * Busy claims are per notification id and independent of listGeneration.
 * Session-owned acknowledged reads survive panel close/reopen until auth/role change.
 * Subscribers receive immediate operation-start publications and coalesced soft completions.
 */
export function createAdminProspectResponseNotificationsController({
  getMounted,
  getAuthUid,
  getRole,
  listNotifications,
  getUnreadCount,
  markNotificationsRead,
} = {}) {
  const state = {
    items: [],
    nextPageToken: null,
    configured: true,
    unreadCount: 0,
    listStatus: 'idle',
    errorKey: null,
    markingClaims: createNotificationMarkingClaimsMap(),
    headIds: new Set(),
    hasLoadedMore: false,
  };

  /** @type {Map<string, string>} */
  const acknowledgedReadAtById = new Map();
  /** @type {Set<(snap: object) => void>} */
  const listeners = new Set();

  let listGeneration = 0;
  let unreadGeneration = 0;
  let loadSeq = 0;
  let claimSeq = 0;
  let unreadMutationGeneration = 0;
  let softRefreshInFlight = false;
  let softRefreshQueued = false;
  /** @type {{ promise: Promise<object>, resolve: (value: object) => void, lastResult: object | null } | null} */
  let softRefreshCycle = null;

  const nextClaimId = () => {
    claimSeq += 1;
    return claimSeq;
  };

  const isActiveAdmin = () => getRole?.() === 'super_admin' && Boolean(getAuthUid?.());

  function beginSoftRefreshCycle() {
    if (softRefreshCycle) return softRefreshCycle;
    let resolve;
    const promise = new Promise((res) => {
      resolve = res;
    });
    softRefreshCycle = { promise, resolve, lastResult: null };
    return softRefreshCycle;
  }

  function finishSoftRefreshCycle(result) {
    if (!softRefreshCycle) return;
    softRefreshCycle.lastResult = result || softRefreshCycle.lastResult;
    const cycle = softRefreshCycle;
    softRefreshCycle = null;
    cycle.resolve(cycle.lastResult || { applied: false });
  }

  const snapshot = () => ({
    items: state.items,
    nextPageToken: state.nextPageToken,
    configured: state.configured,
    unreadCount: state.unreadCount,
    listStatus: state.listStatus,
    errorKey: state.errorKey,
    markingIds: notificationMarkingIdsFromClaims(state.markingClaims),
    listGeneration,
    unreadGeneration,
    unreadMutationGeneration,
    hasLoadedMore: state.hasLoadedMore,
    acknowledgedReadIds: [...acknowledgedReadAtById.keys()],
  });

  function publish() {
    const snap = snapshot();
    for (const listener of [...listeners]) {
      try {
        listener(snap);
      } catch {
        // Listener errors must not break orchestration.
      }
    }
  }

  /**
   * Soft refresh must not clear a pending non-soft loading / loading-more busy state.
   */
  function markListReadyAfterSoft() {
    if (state.listStatus === 'loading' || state.listStatus === 'loading-more') {
      return;
    }
    state.listStatus = 'ready';
  }

  function applyIncomingItems(rawItems, { append = false, soft = false, firstPageNextToken = null } = {}) {
    const reconciled = reconcileNotificationItemsWithAcknowledgedReads(
      rawItems,
      acknowledgedReadAtById,
    );
    if (soft) {
      const merged = mergeSoftRefreshNotificationPages({
        previousItems: state.items,
        headIds: state.headIds,
        firstPageItems: reconciled,
        firstPageNextToken,
        previousNextPageToken: state.nextPageToken,
        hasLoadedMore: state.hasLoadedMore,
      });
      state.items = reconcileNotificationItemsWithAcknowledgedReads(
        merged.items,
        acknowledgedReadAtById,
      );
      state.headIds = merged.headIds;
      state.nextPageToken = merged.nextPageToken;
      state.hasLoadedMore = merged.hasLoadedMore;
      return;
    }
    if (append) {
      state.items = reconcileNotificationItemsWithAcknowledgedReads(
        mergeNotificationItemsById(state.items, reconciled, { append: true }),
        acknowledgedReadAtById,
      );
      state.hasLoadedMore = true;
      state.nextPageToken = firstPageNextToken;
      return;
    }
    state.items = reconciled;
    state.headIds = new Set(
      reconciled.map((row) => (row?.id ? String(row.id) : '')).filter(Boolean),
    );
    state.hasLoadedMore = false;
    state.nextPageToken = firstPageNextToken;
  }

  function invalidateSession() {
    listGeneration += 1;
    unreadGeneration += 1;
    unreadMutationGeneration = unreadGeneration;
    loadSeq += 1;
    softRefreshInFlight = false;
    softRefreshQueued = false;
    if (softRefreshCycle) {
      finishSoftRefreshCycle({ applied: false, reason: 'session-invalidated' });
    }
    clearNotificationMarkingClaims(state.markingClaims);
    acknowledgedReadAtById.clear();
    state.items = [];
    state.nextPageToken = null;
    state.headIds = new Set();
    state.hasLoadedMore = false;
    state.configured = true;
    state.unreadCount = 0;
    state.listStatus = 'idle';
    state.errorKey = null;
    publish();
    return snapshot();
  }

  async function refreshUnread({ preferAfterMutation = false } = {}) {
    if (!isActiveAdmin()) return { applied: false };
    const requestGeneration = ++unreadGeneration;
    if (preferAfterMutation) {
      unreadMutationGeneration = requestGeneration;
    }
    const requestAuthUid = getAuthUid?.() || '';
    const mutationFloor = unreadMutationGeneration;
    try {
      const data = await getUnreadCount();
      if (
        !shouldApplyNotificationResult({
          mounted: Boolean(getMounted?.()),
          requestGeneration,
          activeGeneration: unreadGeneration,
          requestAuthUid,
          activeAuthUid: getAuthUid?.() || '',
        })
      ) {
        return { applied: false };
      }
      if (requestGeneration < mutationFloor) {
        return { applied: false, reason: 'stale-vs-mutation' };
      }
      state.configured = data?.configured !== false;
      state.unreadCount = data?.configured === false ? 0 : Number(data?.unreadCount) || 0;
      publish();
      return { applied: true, unreadCount: state.unreadCount };
    } catch {
      if (
        !shouldApplyNotificationResult({
          mounted: Boolean(getMounted?.()),
          requestGeneration,
          activeGeneration: unreadGeneration,
          requestAuthUid,
          activeAuthUid: getAuthUid?.() || '',
        })
      ) {
        return { applied: false };
      }
      return { applied: false, error: true };
    }
  }

  async function loadList({ append = false, pageToken = null, soft = false } = {}) {
    if (!isActiveAdmin()) return { applied: false };

    if (soft) {
      const cycle = beginSoftRefreshCycle();
      if (softRefreshInFlight) {
        softRefreshQueued = true;
        return {
          applied: false,
          reason: 'refresh-coalesced',
          waitUntilSettled: () => cycle.promise,
        };
      }
      softRefreshInFlight = true;
    }

    const requestGeneration = append || soft ? listGeneration : ++listGeneration;
    const requestAuthUid = getAuthUid?.() || '';
    // Soft refresh must not race-out load-more (or vice versa) via loadSeq.
    // Soft coalescing owns overlapping background refreshes separately.
    const requestLoadSeq = soft ? null : ++loadSeq;
    if (!soft) {
      state.listStatus = append ? 'loading-more' : 'loading';
      state.errorKey = null;
      publish();
    }
    let softResult = null;
    try {
      const data = await listNotifications({
        pageToken: soft ? undefined : pageToken || undefined,
      });
      if (
        !shouldApplyNotificationResult({
          mounted: Boolean(getMounted?.()),
          requestGeneration,
          activeGeneration: listGeneration,
          requestAuthUid,
          activeAuthUid: getAuthUid?.() || '',
        })
      ) {
        if (!soft && requestLoadSeq === loadSeq && getMounted?.()) {
          if (state.listStatus === 'loading' || state.listStatus === 'loading-more') {
            state.listStatus = state.items.length ? 'ready' : 'idle';
            publish();
          }
        }
        softResult = { applied: false };
        return softResult;
      }
      if (requestLoadSeq != null && requestLoadSeq !== loadSeq) {
        softResult = { applied: false };
        return softResult;
      }

      state.configured = data?.configured !== false;
      applyIncomingItems(data?.items || [], {
        append,
        soft,
        firstPageNextToken: data?.nextPageToken ?? null,
      });
      if (soft) {
        markListReadyAfterSoft();
      } else {
        state.listStatus = 'ready';
      }
      if (data?.configured === false) {
        state.unreadCount = 0;
      }
      publish();
      softResult = { applied: true };
      return softResult;
    } catch {
      if (
        !shouldApplyNotificationResult({
          mounted: Boolean(getMounted?.()),
          requestGeneration,
          activeGeneration: listGeneration,
          requestAuthUid,
          activeAuthUid: getAuthUid?.() || '',
        })
      ) {
        if (!soft && requestLoadSeq === loadSeq && getMounted?.()) {
          if (state.listStatus === 'loading' || state.listStatus === 'loading-more') {
            state.listStatus = state.items.length ? 'ready' : 'error';
            publish();
          }
        }
        softResult = { applied: false };
        return softResult;
      }
      if (requestLoadSeq != null && requestLoadSeq !== loadSeq) {
        softResult = { applied: false };
        return softResult;
      }
      if (soft) {
        // Transient background failure preserves loaded content and cursor.
        publish();
        softResult = { applied: false, error: true, preserved: true };
        return softResult;
      }
      state.listStatus = 'error';
      state.errorKey = 'admin.notifications.error';
      publish();
      return { applied: true, error: true };
    } finally {
      if (soft) {
        softRefreshInFlight = false;
        if (softRefreshCycle && softResult) {
          softRefreshCycle.lastResult = softResult;
        }
        if (softRefreshQueued && getMounted?.() && isActiveAdmin()) {
          softRefreshQueued = false;
          // Continue the same cycle so waiters observe the follow-up completion.
          void loadList({ soft: true });
        } else {
          softRefreshQueued = false;
          finishSoftRefreshCycle(softResult || { applied: false });
        }
      }
    }
  }

  async function markRead(notificationIds) {
    const ids = (notificationIds || []).map((id) => String(id || '')).filter(Boolean);
    if (!ids.length || !isActiveAdmin()) return { applied: false };
    const requestGeneration = listGeneration;
    const requestAuthUid = getAuthUid?.() || '';
    const claims = [];
    for (const id of ids) {
      const claimId = claimNotificationMarking(state.markingClaims, id, { nextClaimId });
      if (claimId != null) claims.push({ id, claimId });
    }
    publish();
    try {
      const result = await markNotificationsRead({ notificationIds: ids });
      const sameAuth =
        String(requestAuthUid || '') === String(getAuthUid?.() || '') && Boolean(getMounted?.());
      // Session-owned: remember reads even if the panel closed (listGeneration bumped).
      if (sameAuth && isActiveAdmin()) {
        rememberAcknowledgedNotificationReads(acknowledgedReadAtById, result?.results || []);
        state.items = reconcileNotificationItemsWithAcknowledgedReads(
          state.items,
          acknowledgedReadAtById,
        );
        publish();
      }
      const canApply = shouldApplyNotificationResult({
        mounted: Boolean(getMounted?.()),
        requestGeneration,
        activeGeneration: listGeneration,
        requestAuthUid,
        activeAuthUid: getAuthUid?.() || '',
      });
      if (canApply) {
        await refreshUnread({ preferAfterMutation: true });
        return { applied: true, results: result?.results || [] };
      }
      // Auth still valid but panel closed: still refresh count with mutation preference.
      if (sameAuth && isActiveAdmin()) {
        await refreshUnread({ preferAfterMutation: true });
        return { applied: false, remembered: true, results: result?.results || [] };
      }
      return { applied: false };
    } catch {
      const canApply = shouldApplyNotificationResult({
        mounted: Boolean(getMounted?.()),
        requestGeneration,
        activeGeneration: listGeneration,
        requestAuthUid,
        activeAuthUid: getAuthUid?.() || '',
      });
      if (canApply) {
        state.errorKey = 'admin.notifications.error';
        publish();
        return { applied: true, error: true };
      }
      return { applied: false };
    } finally {
      for (const { id, claimId } of claims) {
        releaseNotificationMarkingClaim(state.markingClaims, id, claimId);
      }
      publish();
    }
  }

  /** Simulate closing the panel: bump list generation without clearing marking claims or session reads. */
  function bumpListGeneration() {
    listGeneration += 1;
    return listGeneration;
  }

  /**
   * @param {(snap: object) => void} listener
   * @returns {() => void} unsubscribe
   */
  function subscribe(listener) {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    listener(snapshot());
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    getState: snapshot,
    subscribe,
    invalidateSession,
    refreshUnread,
    loadList,
    markRead,
    bumpListGeneration,
    getMarkingClaims: () => state.markingClaims,
    getAcknowledgedReadAtById: () => acknowledgedReadAtById,
  };
}
